import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Schedule, ScheduleValveAssignment } from '@/types';
import type { ApiGarden, ApiController, ApiCompany } from '@/types/api';
import type { User } from '@/types';
import { cn } from '@/lib/utils';
import { Settings, List, Trash2 } from 'lucide-react';

interface NativeSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
}

function NativeSelect({ value, onChange, placeholder, options, className, disabled }: NativeSelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          'w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface ScheduleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: Schedule | null;
  onSave: (schedule: Partial<Schedule>) => void;
  isLoading?: boolean;
  gardens?: ApiGarden[];
  controllers?: ApiController[];
  companies?: ApiCompany[];
  currentUser?: User | null;
  isSuperAdmin?: boolean;
}

const daysOfWeek = [
  { value: 0, label: 'Dom', short: 'D' },
  { value: 1, label: 'Lun', short: 'L' },
  { value: 2, label: 'Mar', short: 'M' },
  { value: 3, label: 'Mié', short: 'X' },
  { value: 4, label: 'Jue', short: 'J' },
  { value: 5, label: 'Vie', short: 'V' },
  { value: 6, label: 'Sáb', short: 'S' },
];

export default function ScheduleFormDialog({
  open,
  onOpenChange,
  schedule,
  onSave,
  isLoading = false,
  gardens = [],
  controllers = [],
  companies = [],
  currentUser,
  isSuperAdmin = false,
}: ScheduleFormDialogProps) {
  const [activeTab, setActiveTab] = useState('general');
  const [name, setName] = useState('');
  const [gardenId, setGardenId] = useState('');
  const [companyId, setCompanyId] = useState<string>(''); // Para superadmin
  const [assignments, setAssignments] = useState<ScheduleValveAssignment[]>([]);
  const [startTime, setStartTime] = useState('06:00');
  const [duration, setDuration] = useState(30);
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [enabled, setEnabled] = useState(true);

  const isEditing = !!schedule;

  // Build table data: all controller/valve combinations
  // CAMBIO v0.2.0: Controllers ya no tienen gardenId, mostrar todos los controllers
  // La relación con jardines se establece a través de zonas asignadas
  const getTableData = () => {
    return controllers.flatMap(controller =>
      (controller.valves || []).map(valve => ({
        controllerId: controller.id,
        controllerName: controller.name,
        valveId: valve.id,
        valveName: valve.name,
        channel: valve.channel,
        // Mostrar empresa si está asignada
        companyName: controller.companyId ? `Empresa: ${controller.companyId}` : '-',
      }))
    );
  };

  const tableData = getTableData();

  // Check if a valve is assigned
  const isAssigned = (controllerId: string, valveId: string) => {
    return assignments.some(a => a.controllerId === controllerId && a.valveId === valveId);
  };

  // Toggle assignment
  const toggleAssignment = (controllerId: string, valveId: string) => {
    if (isAssigned(controllerId, valveId)) {
      setAssignments(prev => prev.filter(a => !(a.controllerId === controllerId && a.valveId === valveId)));
    } else {
      setAssignments(prev => [...prev, {
        id: `temp-${Date.now()}-${controllerId}-${valveId}`,
        controllerId,
        valveId,
      }]);
    }
  };

  // Select all valves
  const selectAll = () => {
    const newAssignments = tableData.map(item => ({
      id: `temp-${Date.now()}-${item.controllerId}-${item.valveId}`,
      controllerId: item.controllerId,
      valveId: item.valveId,
    }));
    setAssignments(newAssignments);
  };

  // Clear all assignments
  const clearAll = () => {
    setAssignments([]);
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (schedule) {
        setName(schedule.name);
        setGardenId(schedule.gardenId || '');
        setCompanyId(schedule.companyId || ''); // Inicializar companyId si está editando
        setAssignments(schedule.assignments || []);
        setStartTime(schedule.startTime);
        setDuration(schedule.duration);
        setSelectedDays(schedule.daysOfWeek);
        setEnabled(schedule.enabled);
      } else {
        setName('');
        setGardenId('');
        setCompanyId(''); // Reset companyId
        setAssignments([]);
        setStartTime('06:00');
        setDuration(30);
        setSelectedDays([1, 2, 3, 4, 5]);
        setEnabled(true);
      }
      setActiveTab('general');
    }
  }, [open, schedule]);

  // CAMBIO v0.2.0: Ya no filtramos assignments por gardenId
  // Los controllers no tienen gardenId, la relación se establece a través de zonas
  // Este useEffect se elimina porque ya no es necesario filtrar por gardenId

  const toggleDay = (day: number) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => a - b)
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    onSave({
      id: schedule?.id,
      companyId: isSuperAdmin && companyId ? companyId : schedule?.companyId, // Incluir companyId si es superadmin y se seleccionó
      name,
      gardenId: gardenId || undefined, // Convertir string vacío a undefined, que luego se convertirá a null
      assignments,
      startTime,
      duration,
      daysOfWeek: selectedDays,
      enabled,
    });
    
    onOpenChange(false);
  };

  const isValid = name.trim() && startTime && duration > 0 && selectedDays.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Programación' : 'Nueva Programación'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifica los datos de la programación de riego.'
              : 'Configura una nueva programación de riego automático.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="general" className="gap-2">
                <Settings className="w-4 h-4" />
                General
              </TabsTrigger>
              <TabsTrigger value="assignments" className="gap-2">
                <List className="w-4 h-4" />
                Asignaciones
                {assignments.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary text-primary-foreground">
                    {assignments.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="flex-1 overflow-y-auto py-4 space-y-6">
              {/* Empresa (solo para superadmin) */}
              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="company">Empresa</Label>
                  <NativeSelect
                    value={companyId}
                    onChange={setCompanyId}
                    placeholder="Seleccionar empresa (opcional para programación global)"
                    options={[
                      { value: '', label: 'Sin empresa (programación global)' },
                      ...companies.map(c => ({ value: c.id, label: c.name }))
                    ]}
                  />
                  <p className="text-xs text-muted-foreground">
                    Selecciona una empresa o déjalo vacío para crear una programación global
                  </p>
                </div>
              )}
              
              {/* Nombre */}
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Riego matutino césped"
                  required
                />
              </div>

              {/* Jardín - CAMBIO v0.2.0: Ya no filtra controllers, solo para referencia */}
              <div className="space-y-2">
                <Label htmlFor="garden">Jardín (opcional, para referencia)</Label>
                <select
                  id="garden"
                  value={gardenId}
                  onChange={(e) => setGardenId(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Sin jardín asignado</option>
                  {gardens.map((garden) => (
                    <option key={garden.id} value={garden.id}>
                      {garden.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Nota: Los controladores ya no se filtran por jardín. Selecciona los controladores directamente en la pestaña "Asignaciones".
                </p>
              </div>

              {/* Hora y Duración */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startTime">Hora de inicio *</Label>
                  <Input
                    id="startTime"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duración (min) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    min={1}
                    max={180}
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                    required
                  />
                </div>
              </div>

              {/* Días de la semana */}
              <div className="space-y-2">
                <Label>Días de la semana *</Label>
                <div className="flex gap-2 flex-wrap">
                  {daysOfWeek.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={cn(
                        'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                        selectedDays.includes(day.value)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      )}
                      title={day.label}
                    >
                      {day.short}
                    </button>
                  ))}
                </div>
                {selectedDays.length === 0 && (
                  <p className="text-sm text-destructive">Selecciona al menos un día</p>
                )}
              </div>

              {/* Habilitado */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enabled">Programación activa</Label>
                  <p className="text-sm text-muted-foreground">
                    Activa o desactiva esta programación
                  </p>
                </div>
                <Switch
                  id="enabled"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                />
              </div>
            </TabsContent>

            <TabsContent value="assignments" className="flex-1 overflow-hidden flex flex-col min-h-0 py-4">
              {/* Actions bar */}
              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <p className="text-sm text-muted-foreground">
                  Selecciona las controladoras y válvulas que usarán esta programación
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAll}>
                    Seleccionar todo
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={clearAll}
                    disabled={assignments.length === 0}
                    className="gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Limpiar
                  </Button>
                </div>
              </div>

              {/* Table: contenedor con scroll para poder ver y gestionar todas las controladoras */}
              <div className="flex-1 min-h-0 border rounded-lg overflow-y-auto overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Controladora</TableHead>
                      <TableHead>Válvula</TableHead>
                      <TableHead className="w-20 text-center">Canal</TableHead>
                      {!gardenId && <TableHead>Jardín</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={gardenId ? 4 : 5} className="text-center py-8 text-muted-foreground">
                          No hay controladoras disponibles
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((item) => (
                        <TableRow 
                          key={`${item.controllerId}-${item.valveId}`}
                          className={cn(
                            "cursor-pointer transition-colors",
                            isAssigned(item.controllerId, item.valveId) && "bg-primary/5"
                          )}
                          onClick={() => toggleAssignment(item.controllerId, item.valveId)}
                        >
                          <TableCell className="text-center">
                            <Checkbox
                              checked={isAssigned(item.controllerId, item.valveId)}
                              onCheckedChange={() => toggleAssignment(item.controllerId, item.valveId)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{item.controllerName}</TableCell>
                          <TableCell>{item.valveName}</TableCell>
                          <TableCell className="text-center">{item.channel}</TableCell>
                          {!gardenId && <TableCell className="text-muted-foreground">{item.companyName}</TableCell>}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Summary */}
              <div className="mt-3 p-3 bg-muted/50 rounded-lg flex-shrink-0">
                <p className="text-sm">
                  <span className="font-medium">{assignments.length}</span> válvula(s) asignada(s)
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!isValid || isLoading}>
              {isLoading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear programación'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
